/**
 * The purr, synthesised.
 *
 * Synthesised rather than sampled because the build inlines every asset as
 * base64 and the page is ~4 MB already; a loop long enough not to read as a
 * loop would be hundreds of kilobytes for one sound.  And a purr is a lucky
 * thing to have to synthesise: it is not a pitch, it is a RHYTHM.  A domestic
 * cat purrs by fluttering the glottis 20-30 times a second, on the in-breath
 * and the out-breath alike, and each flutter is a little burst of broadband
 * chest noise -- so the recipe is "a low rumble bed, amplitude-gated ~26
 * times a second, swelling and dipping with a slow breath".  Every number
 * below is one of those three things.
 *
 *   PULSE_HZ    26   the glottal flutter.  Published measurements put house
 *                    cats at 21-30 Hz; 26 sits mid-range.  This is the single
 *                    number that makes it a purr and not a hum.
 *   PULSE_SWING 2.2  the flutter runs a couple of Hz faster on the in-breath
 *                    than the out -- the drift is half of why a real purr
 *                    never sounds mechanical.
 *   BREATH_HZ   0.4  the breath itself: ~24 breaths/min, a relaxed cat.  It
 *                    modulates loudness (the swell) and PULSE_SWING (the
 *                    drift), and its shaping curve dips at each reversal --
 *                    the tiny catch where the breath turns around.
 *   BED_HZ      88   centre of the noise bed's bandpass.  A purr's energy
 *                    sits roughly 50-150 Hz; this is the "chest" of the
 *                    sound.  A 46 Hz sine underneath adds the weight you feel
 *                    more than hear (and is honestly lost on laptop
 *                    speakers, which is why the bed and the hiss exist).
 *   ~850 Hz     a whisper of higher-band noise, breath-shaped but NOT
 *                    pulse-gated -- air through the nose is smooth even while
 *                    the glottis flutters.  It is most of what a phone
 *                    speaker gets to reproduce.
 *
 * Autoplay policy: an AudioContext created before a user gesture starts
 * suspended, so the constructor touches no audio at all and resume() -- called
 * from begin()'s real click or keypress -- builds everything.  Every public
 * method is a no-op until then, and a context that cannot be created (no
 * WebAudio, a hostile iframe) leaves a silent but fully working game.
 */
import { PURR_AT } from "@catworld/shared";
import { getAdopting } from "./legacy-storage.js";

const PULSE_HZ = 26;
const PULSE_SWING = 2.2;
const BREATH_HZ = 0.4;
const BED_HZ = 88;
const FUND_HZ = 46;
/** Output lowpass: at faint intensity the purr is nearly all felt (150 Hz);
    at full it opens to ~340 Hz and the flutter's texture becomes audible. */
const LP_BASE = 150;
const LP_SPAN = 190;
/** Master ceiling.  The purr is ambience, not an event: at full intensity it
    should sit under speech volume, not compete with it. */
const MASTER = 0.5;

const MUTE_KEY = "catworld.purr.muted";

/** The pre-rename spelling, adopted on first read (legacy-storage.js) so a
    player who muted the cat stays muted. */
const LEGACY_MUTE_KEY = "lilysworld.purr.muted";

/** localStorage, or null where even touching it throws (sandboxed iframes,
    some private modes) -- the same posture as customizer/store.js. */
function storage() {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

/**
 * Sine -> glottal pulse.  (x+1)/2 folds the LFO to 0..1, the 2.6 power
 * narrows each cycle's top into a ~13 ms thump with ~25 ms of trough -- a
 * flutter, where the raw sine's 50% duty is exactly the "phone on vibrate"
 * this must not be.  The 0.12 floor keeps a continuous bed under the pulses:
 * gated to true silence between thumps it turns into a distant helicopter.
 */
function pulseCurve() {
  const n = 512, c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = 0.12 + 0.88 * Math.pow((x + 1) / 2, 2.6);
  }
  return c;
}

/**
 * Sine -> breath envelope, 0.52..1.0.  |x|^0.7 dips at both zero crossings
 * (the catch at each end of a breath, twice per cycle); the max(0,x) term
 * makes the positive half -- call it the out-breath -- carry more air, which
 * real purrs audibly do.
 */
function breathCurve() {
  const n = 512, c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = 0.52 + 0.36 * Math.pow(Math.abs(x), 0.7) + 0.12 * Math.max(0, x);
  }
  return c;
}

export class Purr {
  constructor() {
    this.ctx = null;            // no AudioContext until a real user gesture
    this.n = null;              // the node handles, once built
    this.running = false;
    this.intensity = 0;
    this.shown = -1;            // last intensity actually applied
    this.muted = getAdopting(storage(), MUTE_KEY, LEGACY_MUTE_KEY) === "1";
    this.muteEl = null;
  }

  /** Call from a real user gesture (main.js's begin()).  Safe to call again:
      it also re-resumes a context the browser has suspended, e.g. after the
      tab was backgrounded on mobile. */
  resume() {
    try {
      if (!this.ctx) {
        const AC = globalThis.AudioContext || globalThis.webkitAudioContext;
        if (!AC) return;
        this.ctx = new AC();
        this.build();
      }
      if (this.ctx.state === "suspended") this.ctx.resume().catch(() => {});
      this.apply(true);
    } catch {
      // No audio here.  The purr is feedback, not the game; nothing throws on.
      this.ctx = null;
      this.n = null;
    }
  }

  build() {
    const ctx = this.ctx;

    // The one noise source feeds both bands.  Two seconds of white noise on
    // loop: the loop point is inaudible through an 88 Hz bandpass, and a
    // longer buffer is memory for nothing.
    const sr = ctx.sampleRate;
    const buf = ctx.createBuffer(1, 2 * sr, sr);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    noise.loop = true;

    // Chest bed: noise through the 88 Hz bandpass, plus the 46 Hz sine.
    const bed = ctx.createBiquadFilter();
    bed.type = "bandpass";
    bed.frequency.value = BED_HZ;
    bed.Q.value = 0.8;
    const bedGain = ctx.createGain();
    bedGain.gain.value = 1.6;   // the bandpass eats most of the noise's level
    const fund = ctx.createOscillator();
    fund.type = "sine";
    fund.frequency.value = FUND_HZ;
    const fundGain = ctx.createGain();
    fundGain.gain.value = 0.45;

    // The flutter: `pulsed` idles at gain 0 and the shaped LFO (0.12..1.0)
    // rides on top, so the LFO's own value IS the gain.
    const pulsed = ctx.createGain();
    pulsed.gain.value = 0;
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = PULSE_HZ;
    const pulseShape = ctx.createWaveShaper();
    pulseShape.curve = pulseCurve();
    lfo.connect(pulseShape).connect(pulsed.gain);

    // The breath: one slow sine, shaped, driving loudness on `breathed`
    // (gain 0 + envelope, same trick) and the flutter's rate drift.
    const breath = ctx.createOscillator();
    breath.type = "sine";
    breath.frequency.value = BREATH_HZ;
    const breathShape = ctx.createWaveShaper();
    breathShape.curve = breathCurve();
    const breathed = ctx.createGain();
    breathed.gain.value = 0;
    breath.connect(breathShape).connect(breathed.gain);
    const rate = ctx.createGain();
    rate.gain.value = PULSE_SWING;
    breath.connect(rate).connect(lfo.frequency);

    // Nose air: the higher noise band joins AFTER the pulse gate -- breath is
    // smooth even while the glottis flutters -- but before the breath swell,
    // which it must ride.
    const hband = ctx.createBiquadFilter();
    hband.type = "bandpass";
    hband.frequency.value = 850;
    hband.Q.value = 0.7;
    const hiss = ctx.createGain();
    hiss.gain.value = 0.02;

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = LP_BASE;
    lp.Q.value = 0.4;

    const out = ctx.createGain();   // intensity and mute land here
    out.gain.value = 0;

    noise.connect(bed).connect(bedGain).connect(pulsed);
    fund.connect(fundGain).connect(pulsed);
    pulsed.connect(lp).connect(breathed);
    noise.connect(hband).connect(hiss).connect(breathed);
    breathed.connect(out).connect(ctx.destination);

    // Sources run for the life of the page.  A silent graph of one noise
    // buffer and three oscillators costs a rounding error of CPU, and
    // start/stop churn on AudioBufferSourceNodes is where clicks come from.
    noise.start();
    fund.start();
    lfo.start();
    breath.start();

    this.n = { out, lp, hiss };
  }

  start() {
    this.running = true;
    this.apply(true);
  }

  stop() {
    this.running = false;
    this.apply(true);
  }

  /** Purr intensity 0..1: just past PURR_AT is a faint rumble, 1 is
      unmistakable.  Safe to call every frame -- quantised and cached. */
  setIntensity(v) {
    this.intensity = Math.max(0, Math.min(1, v || 0));
    this.apply(false);
  }

  /** One call for the loop: happiness in, purr out.  Maps the span above
      PURR_AT onto 0..1 and handles start/stop, so main.js's wiring is a
      single line off the value it already has. */
  setFromHappiness(h) {
    const v = (h - PURR_AT) / (1 - PURR_AT);
    if (v > 0 && !this.running) this.start();
    else if (v <= 0 && this.running) this.stop();
    this.setIntensity(v);
  }

  /** Push the current state into the graph.  `force` skips the cache -- for
      start/stop/mute edges, where intensity alone has not changed. */
  apply(force) {
    if (!this.n) return;
    const step = Math.round(this.intensity * 50) / 50;
    if (!force && step === this.shown) return;
    this.shown = step;
    const audible = this.running && !this.muted && step > 0;
    // ^1.6 spends the bottom of the range slowly: happiness just over the
    // line should be a purr you lean in for, not half volume already.
    const gain = audible ? (0.05 + 0.95 * Math.pow(step, 1.6)) * MASTER : 0;
    const t = this.ctx.currentTime;
    // setTargetAtTime, never a bare value write: a purr that snaps on or off
    // clicks, and a cat's purr fades in the way the τ=0.35 exponential does.
    this.n.out.gain.setTargetAtTime(gain, t, 0.35);
    this.n.lp.frequency.setTargetAtTime(LP_BASE + LP_SPAN * step, t, 0.35);
    this.n.hiss.gain.setTargetAtTime(0.012 + 0.045 * step, t, 0.35);
  }

  // ---- mute, persisted -----------------------------------------------------

  setMuted(muted) {
    this.muted = !!muted;
    // Best effort, store.js's posture: a full or absent storage loses the
    // preference across reloads, never the toggle in front of you.
    try {
      storage()?.setItem(MUTE_KEY, this.muted ? "1" : "0");
    } catch {}
    this.apply(true);
    this.reflectMute();
  }

  toggleMute() {
    this.setMuted(!this.muted);
    return this.muted;
  }

  /** Adopt the #mute button: label, pressed state, click.  Optional -- the
      purr works without any UI attached. */
  bindMute(el) {
    if (!el) return;
    this.muteEl = el;
    el.addEventListener("click", () => this.toggleMute());
    this.reflectMute();
  }

  reflectMute() {
    const el = this.muteEl;
    if (!el) return;
    el.textContent = this.muted ? "purr off" : "purr on";
    el.setAttribute("aria-pressed", String(this.muted));
    el.classList.toggle("muted", this.muted);
  }
}
